import React from 'react'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import type { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { Icon } from '@frontend/pole-ui/lib/components/Icon'
import { ShevronRightIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronRightIcon'
import className from 'classnames'
import { Button, Image } from '@/components/ui'
import { pushGtmEventOnClickApplyOnFundingOfferCard } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { LOAN_PRODUCTS } from '@/pages/Financing/components/FinancingProducts/components/LoanProducts/constants'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferType } from '@/types'
import { ELoanProductName } from '@/types'
import { useOpenApplyingFinancingOfferModal } from '@/utils/hooks/applyingFinancingOffer/useOpenApplyingFinancingOfferModal'
import { useWebpExtension } from '@/utils/hooks/image'
import { messages } from './messages'
import type { IFinancingProductOtherProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
})

export const FinancingProductOther: React.FC<IFinancingProductOtherProps> = ({ product }) => {
  const imageExt = useWebpExtension()
  const { userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const isAccent = product.productId === ELoanProductName.EXPRESS_MFO

  const productInfo = LOAN_PRODUCTS[product.productId]

  const { openApplyingFinancingOfferModal } = useOpenApplyingFinancingOfferModal({
    offerType: product.productId as EFinancingOfferType,
    productId: product.productId as FundingProduct,
  })

  const onActionOrder = () => {
    openApplyingFinancingOfferModal()
  }

  if (!productInfo) {
    return null
  }

  const image = `${productInfo.brand.logo}Mobile.${imageExt}`

  return (
    <div className={className('financingProductOther', productInfo.productId, { 'financingProductOther--accent': isAccent })}>
      <div className="financingProductOtherHeader">
        <Image className="financingProductOtherHeader__brand" src={image} isInStatic />
        <CommonLink url={productInfo.url}>
          <h5 className="financingProductOtherHeader__title">{product.title}</h5>
        </CommonLink>
        {isAccent && <Image className="financingProductOtherHeader__farmerChoice" src="financing/farmersChoiceMobile.svg" isInStatic />}
      </div>

      <div className="financingProductOtherConditions">
        {product.detailCards.map((detail, index) => (
          <div className="financingProductOtherCondition" key={index}>
            <div className="financingProductOtherCondition__label text_small text_super-small-sm">{detail.name}</div>
            <div className="text_small text_super-small-sm">{detail.value}</div>
          </div>
        ))}
      </div>

      <div className="financingProductOtherActions">
        <Button
          modifiers={['green', 'full-width-always', 'small']}
          onClick={() => {
            onActionOrder()
            pushGtmEventOnClickApplyOnFundingOfferCard(userGtmInfo, userRegion.name, product.title, 'tab_click')
          }}
        >
          {messages.actions.sendOrder}
        </Button>
        <CommonLink
          url={productInfo.url}
          onClick={() => pushGtmEventOnClickApplyOnFundingOfferCard(userGtmInfo, userRegion.name, product.title, 'details_tab_click')}
        >
          <Button modifiers={['outline-gray', 'full-width-always', 'small']}>
            {messages.actions.more} <Icon component={<ShevronRightIcon />} />
          </Button>
        </CommonLink>
      </div>
    </div>
  )
}
